import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { SIARFlowIndicator } from "@/components/admin/SIARFlowIndicator";
import { 
  CheckCircle2, 
  XCircle, 
  Clock, 
  TrendingUp, 
  TrendingDown,
  BarChart3,
  Calendar,
  AlertTriangle,
  Loader2,
  Eye,
  Target,
  Activity,
  RefreshCw,
  Zap,
  ArrowRight
} from "lucide-react";
import { format, subDays } from "date-fns";
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from "recharts";

type VerificationOutcome = {
  id: string;
  verification_type: string;
  source_id: string;
  outcome: string;
  effectiveness_score: number | null;
  before_metrics: Record<string, unknown>;
  after_metrics: Record<string, unknown>;
  notes: string | null;
  scheduled_for: string | null;
  verified_at: string | null;
  follow_up_required: boolean;
  created_at: string;
};

type InterventionExperiment = {
  id: string;
  name: string;
  intervention_type: string;
  is_active: boolean;
  start_date: string;
  end_date: string | null;
  effect_size: number | null;
  p_value: number | null;
  conclusion: string | null;
  treatment_group_size: number;
  control_group_size: number;
};

type DiagnosticAccuracy = {
  id: string;
  diagnostic_type: string;
  predicted_outcome: string | null;
  actual_outcome: string | null;
  predicted_confidence: number | null;
  accuracy_delta: number | null;
  created_at: string;
};

const COLORS = ["#22c55e", "#eab308", "#ef4444", "#6b7280"];

export default function ResultsDashboard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedVerification, setSelectedVerification] = useState<VerificationOutcome | null>(null);
  const [verificationNotes, setVerificationNotes] = useState("");
  const [verificationOutcome, setVerificationOutcome] = useState<string>("effective");

  const { data: verifications, isLoading: verificationsLoading } = useQuery({
    queryKey: ["verification-outcomes"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("verification_outcomes")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as VerificationOutcome[];
    }
  });

  const { data: experiments } = useQuery({
    queryKey: ["intervention-experiments-results"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("intervention_experiments")
        .select("*")
        .order("start_date", { ascending: false });
      if (error) throw error;
      return data as InterventionExperiment[];
    }
  });

  const { data: accuracyLogs } = useQuery({
    queryKey: ["diagnostic-accuracy"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("diagnostic_accuracy_log")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(100);
      if (error) throw error;
      return data as DiagnosticAccuracy[];
    }
  });

  const { data: pendingTasks } = useQuery({
    queryKey: ["pending-verifications"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("supervisor_tasks")
        .select("*")
        .eq("status", "completed")
        .eq("verification_required", true);
      if (error) throw error;
      return data;
    }
  });

  const recordVerification = useMutation({
    mutationFn: async ({ 
      sourceId, 
      outcome, 
      notes,
      effectivenessScore 
    }: { 
      sourceId: string; 
      outcome: string; 
      notes: string;
      effectivenessScore: number;
    }) => {
      // Get company_id from user profile
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: profile } = await supabase
        .from('worker_profiles')
        .select('company_id')
        .eq('user_id', user.id)
        .single();

      const { error } = await supabase
        .from("verification_outcomes")
        .insert({
          company_id: profile?.company_id,
          verification_type: "supervisor_task",
          source_id: sourceId,
          outcome,
          notes,
          effectiveness_score: effectivenessScore,
          verified_at: new Date().toISOString()
        });
      if (error) throw error;

      // Also log to diagnostic accuracy for feedback loop
      await supabase.from("diagnostic_accuracy_log").insert({
        company_id: profile?.company_id,
        diagnostic_type: "supervisor_task",
        diagnostic_id: sourceId,
        actual_outcome: outcome,
        outcome_date: new Date().toISOString()
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["verification-outcomes"] });
      queryClient.invalidateQueries({ queryKey: ["diagnostic-accuracy"] });
      queryClient.invalidateQueries({ queryKey: ["pending-verifications"] });
      setSelectedVerification(null);
      setVerificationNotes("");
      toast.success("Verification recorded");
    },
    onError: () => toast.error("Failed to record verification")
  });

  // Calculate stats
  const stats = {
    totalVerifications: verifications?.length || 0,
    effective: verifications?.filter(v => v.outcome === "effective").length || 0,
    partiallyEffective: verifications?.filter(v => v.outcome === "partially_effective").length || 0,
    ineffective: verifications?.filter(v => v.outcome === "ineffective").length || 0,
    pending: verifications?.filter(v => v.outcome === "pending").length || 0,
    avgEffectiveness: verifications?.length 
      ? Math.round(
          verifications
            .filter(v => v.effectiveness_score !== null)
            .reduce((sum, v) => sum + (v.effectiveness_score || 0), 0) / 
          verifications.filter(v => v.effectiveness_score !== null).length
        )
      : 0
  };

  const effectivenessRate = stats.totalVerifications > 0 
    ? Math.round(((stats.effective + stats.partiallyEffective * 0.5) / (stats.totalVerifications - stats.pending)) * 100)
    : 0;

  // Pie chart data
  const outcomeData = [
    { name: "Effective", value: stats.effective, color: "#22c55e" },
    { name: "Partial", value: stats.partiallyEffective, color: "#eab308" },
    { name: "Ineffective", value: stats.ineffective, color: "#ef4444" },
    { name: "Pending", value: stats.pending, color: "#6b7280" }
  ].filter(d => d.value > 0);

  // Diagnostic accuracy by type
  const accuracyByType = accuracyLogs?.reduce((acc, log) => {
    if (!acc[log.diagnostic_type]) {
      acc[log.diagnostic_type] = { total: 0, accurate: 0 };
    }
    acc[log.diagnostic_type].total++;
    if (log.accuracy_delta !== null && Math.abs(log.accuracy_delta) < 0.2) {
      acc[log.diagnostic_type].accurate++;
    }
    return acc;
  }, {} as Record<string, { total: number; accurate: number }>) || {};

  return (
    <div className="space-y-6">
      {/* SIAR Flow Indicator */}
      <SIARFlowIndicator 
        currentStep="results" 
        context={{
          resultCount: stats.totalVerifications,
          actionCount: pendingTasks?.length || 0
        }}
      />

      {/* Header with CTAs */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Target className="h-7 w-7 text-primary" />
            Results Dashboard
          </h2>
          <p className="text-muted-foreground">
            Verify effectiveness and close the feedback loop
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate('/admin/action-queue')} className="gap-2">
            <Zap className="h-4 w-4" />
            Back to Actions
          </Button>
          <Button onClick={() => navigate('/admin')} className="gap-2">
            <RefreshCw className="h-4 w-4" />
            New Cycle
          </Button>
        </div>
      </div>

      {/* Feedback Loop Banner */}
      <Card className="bg-gradient-to-r from-primary/5 to-primary/10 border-primary/20">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-full bg-primary/10">
              <RefreshCw className="h-6 w-6 text-primary" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold">Feedback Loop Active</h3>
              <p className="text-sm text-muted-foreground">
                Verification outcomes automatically improve diagnostic confidence scores
              </p>
            </div>
            <Badge variant="outline" className="bg-primary/10">
              {stats.totalVerifications} verifications recorded
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="border-primary/20">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <Target className="h-5 w-5 text-primary" />
              <div>
                <p className="text-2xl font-bold">{effectivenessRate}%</p>
                <p className="text-xs text-muted-foreground">Effectiveness Rate</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-500" />
              <div>
                <p className="text-2xl font-bold">{stats.effective}</p>
                <p className="text-xs text-muted-foreground">Effective</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-500" />
              <div>
                <p className="text-2xl font-bold">{stats.partiallyEffective}</p>
                <p className="text-xs text-muted-foreground">Partial</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <XCircle className="h-5 w-5 text-destructive" />
              <div>
                <p className="text-2xl font-bold">{stats.ineffective}</p>
                <p className="text-xs text-muted-foreground">Ineffective</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-orange-500/20 bg-orange-500/5">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-orange-500" />
              <div>
                <p className="text-2xl font-bold text-orange-600">{pendingTasks?.length || 0}</p>
                <p className="text-xs text-muted-foreground">Awaiting Verification</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Tabs defaultValue="verifications" className="space-y-4">
        <TabsList>
          <TabsTrigger value="verifications" className="gap-2">
            <Eye className="h-4 w-4" />
            Verifications
          </TabsTrigger>
          <TabsTrigger value="experiments" className="gap-2">
            <BarChart3 className="h-4 w-4" />
            A/B Experiments
          </TabsTrigger>
          <TabsTrigger value="accuracy" className="gap-2">
            <Target className="h-4 w-4" />
            Diagnostic Accuracy
          </TabsTrigger>
        </TabsList>

        {/* Verifications Tab */}
        <TabsContent value="verifications" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Outcome Distribution */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Outcome Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                {outcomeData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie
                        data={outcomeData}
                        cx="50%"
                        cy="50%"
                        innerRadius={40}
                        outerRadius={80}
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      >
                        {outcomeData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[200px] flex items-center justify-center text-muted-foreground">
                    No verification data yet
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Pending Verifications */}
            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle className="text-sm">Pending Verifications</CardTitle>
              </CardHeader>
              <CardContent>
                {pendingTasks?.length === 0 ? (
                  <p className="text-muted-foreground text-sm">
                    No tasks awaiting verification
                  </p>
                ) : (
                  <div className="space-y-2 max-h-[200px] overflow-y-auto">
                    {pendingTasks?.slice(0, 5).map((task: any) => (
                      <div 
                        key={task.id} 
                        className="flex items-center justify-between p-2 bg-muted/50 rounded"
                      >
                        <div>
                          <p className="text-sm font-medium">{task.title}</p>
                          <p className="text-xs text-muted-foreground">
                            Completed {task.completed_at ? format(new Date(task.completed_at), "MMM d") : "recently"}
                          </p>
                        </div>
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button size="sm" variant="outline">Verify</Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>Record Verification</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4">
                              <div>
                                <p className="font-medium">{task.title}</p>
                                <p className="text-sm text-muted-foreground">{task.description}</p>
                              </div>
                              <Select
                                value={verificationOutcome}
                                onValueChange={setVerificationOutcome}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Outcome" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="effective">Effective - Problem fully resolved</SelectItem>
                                  <SelectItem value="partially_effective">Partially Effective - Some improvement</SelectItem>
                                  <SelectItem value="ineffective">Ineffective - No improvement</SelectItem>
                                </SelectContent>
                              </Select>
                              <Textarea
                                placeholder="Notes on verification..."
                                value={verificationNotes}
                                onChange={(e) => setVerificationNotes(e.target.value)}
                              />
                              <Button 
                                className="w-full"
                                onClick={() => recordVerification.mutate({
                                  sourceId: task.id,
                                  outcome: verificationOutcome,
                                  notes: verificationNotes,
                                  effectivenessScore: verificationOutcome === "effective" ? 100 : 
                                    verificationOutcome === "partially_effective" ? 50 : 0
                                })}
                                disabled={recordVerification.isPending}
                              >
                                {recordVerification.isPending ? "Recording..." : "Record Verification"}
                              </Button>
                            </div>
                          </DialogContent>
                        </Dialog>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Recent Verifications List */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Recent Verifications</CardTitle>
            </CardHeader>
            <CardContent>
              {verificationsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : verifications?.length === 0 ? (
                <p className="text-muted-foreground text-center py-4">
                  No verifications recorded yet
                </p>
              ) : (
                <div className="space-y-2">
                  {verifications?.slice(0, 10).map((v) => (
                    <div key={v.id} className="flex items-center justify-between p-3 border rounded">
                      <div className="flex items-center gap-3">
                        {v.outcome === "effective" && <CheckCircle2 className="h-5 w-5 text-green-500" />}
                        {v.outcome === "partially_effective" && <AlertTriangle className="h-5 w-5 text-yellow-500" />}
                        {v.outcome === "ineffective" && <XCircle className="h-5 w-5 text-destructive" />}
                        {v.outcome === "pending" && <Clock className="h-5 w-5 text-muted-foreground" />}
                        <div>
                          <p className="text-sm font-medium capitalize">
                            {v.verification_type.replace("_", " ")}
                          </p>
                          {v.notes && (
                            <p className="text-xs text-muted-foreground">{v.notes}</p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        {v.effectiveness_score !== null && (
                          <Badge variant={v.effectiveness_score >= 70 ? "default" : "secondary"}>
                            {v.effectiveness_score}%
                          </Badge>
                        )}
                        <p className="text-xs text-muted-foreground mt-1">
                          {v.verified_at ? format(new Date(v.verified_at), "MMM d, yyyy") : "Pending"}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* A/B Experiments Tab */}
        <TabsContent value="experiments" className="space-y-4">
          {experiments?.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                No A/B experiments have been run yet. Create experiments from the Interventions page.
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {experiments?.map((exp) => (
                <Card key={exp.id}>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-base">{exp.name}</CardTitle>
                        <p className="text-sm text-muted-foreground capitalize">
                          {exp.intervention_type.replace("_", " ")}
                        </p>
                      </div>
                      <Badge variant={exp.is_active ? "default" : "secondary"}>
                        {exp.is_active ? "Active" : "Completed"}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-xs text-muted-foreground">Treatment Group</p>
                        <p className="text-lg font-bold">{exp.treatment_group_size}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Control Group</p>
                        <p className="text-lg font-bold">{exp.control_group_size}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Effect Size</p>
                        <p className="text-lg font-bold">
                          {exp.effect_size !== null ? `${(exp.effect_size * 100).toFixed(1)}%` : "—"}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">P-Value</p>
                        <p className="text-lg font-bold">
                          {exp.p_value !== null ? exp.p_value.toFixed(3) : "—"}
                        </p>
                      </div>
                    </div>
                    {exp.conclusion && (
                      <div className="mt-4 p-3 bg-muted rounded">
                        <p className="text-sm font-medium">Conclusion</p>
                        <p className="text-sm text-muted-foreground">{exp.conclusion}</p>
                      </div>
                    )}
                    <div className="mt-4 flex items-center gap-4 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        Started: {format(new Date(exp.start_date), "MMM d, yyyy")}
                      </span>
                      {exp.end_date && (
                        <span>Ended: {format(new Date(exp.end_date), "MMM d, yyyy")}</span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Diagnostic Accuracy Tab */}
        <TabsContent value="accuracy" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Diagnostic Accuracy by Type</CardTitle>
              <p className="text-xs text-muted-foreground">
                How accurate are our predictions? This feeds back into algorithm improvements.
              </p>
            </CardHeader>
            <CardContent>
              {Object.keys(accuracyByType).length === 0 ? (
                <p className="text-muted-foreground text-center py-4">
                  No accuracy data yet. Record verifications to build accuracy metrics.
                </p>
              ) : (
                <div className="space-y-4">
                  {Object.entries(accuracyByType).map(([type, data]) => {
                    const accuracy = data.total > 0 ? Math.round((data.accurate / data.total) * 100) : 0;
                    return (
                      <div key={type} className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium capitalize">
                            {type.replace("_", " ")}
                          </span>
                          <span className="text-sm font-bold">{accuracy}%</span>
                        </div>
                        <Progress value={accuracy} className="h-2" />
                        <p className="text-xs text-muted-foreground">
                          {data.accurate} accurate out of {data.total} predictions
                        </p>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Feedback Loop Status */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Feedback Loop Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 bg-muted/50 rounded">
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                  <div>
                    <p className="text-sm font-medium">Verification Recording</p>
                    <p className="text-xs text-muted-foreground">Outcomes are being tracked</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-muted/50 rounded">
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                  <div>
                    <p className="text-sm font-medium">Accuracy Logging</p>
                    <p className="text-xs text-muted-foreground">Predictions vs actuals being compared</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-muted/50 rounded">
                  <TrendingUp className="h-5 w-5 text-blue-500" />
                  <div>
                    <p className="text-sm font-medium">Algorithm Improvement</p>
                    <p className="text-xs text-muted-foreground">
                      Confidence scores adjust based on accuracy data
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
